import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Input,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  useColorModeValue,
  Portal,
  Kbd,
} from '@chakra-ui/react'
import { FiFolder, FiSearch, FiArrowRight } from 'react-icons/fi'
import { useNavigation } from '@/hooks/useNavigation'
import { Folder } from '@/types'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
}

interface FolderWithPath extends Folder {
  fullPath: string
}

interface FolderSearchResult {
  folder: FolderWithPath
  score: number
  matches: number[]
}

// Simple fuzzy search function
function fuzzyMatch(needle: string, haystack: string): { score: number; matches: number[] } {
  if (!needle) return { score: 1, matches: [] }

  const needleLower = needle.toLowerCase()
  const haystackLower = haystack.toLowerCase()

  if (haystackLower.includes(needleLower)) {
    const startIndex = haystackLower.indexOf(needleLower)
    const matches = Array.from({ length: needle.length }, (_, i) => startIndex + i)
    const score = 1 - startIndex / haystack.length
    return { score, matches }
  }

  let needleIndex = 0
  const matches: number[] = []
  let score = 0

  for (let i = 0; i < haystack.length && needleIndex < needle.length; i++) {
    if (haystackLower[i] === needleLower[needleIndex]) {
      matches.push(i)
      needleIndex++
      score += 1
    }
  }

  if (needleIndex !== needle.length) {
    return { score: 0, matches: [] }
  }

  // Higher score for more consecutive matches
  return { score: score / haystack.length, matches }
}

// Function to build folder paths and flatten the tree
function buildFolderPaths(folders: Folder[], parentPath: string = ''): FolderWithPath[] {
  const result: FolderWithPath[] = []

  folders.forEach((folder) => {
    const currentPath = parentPath ? `${parentPath}/${folder.name}` : folder.name
    const folderWithPath: FolderWithPath = {
      ...folder,
      fullPath: currentPath,
    }

    result.push(folderWithPath)

    // Recursively add subfolders
    const children = folder.subfolders || folder.children || []
    if (children.length > 0) {
      result.push(...buildFolderPaths(children, currentPath))
    }
  })

  return result
}

function HighlightedText({ text, matches }: { text: string; matches: number[] }) {
  if (!matches.length) return <>{text}</>

  const parts: React.ReactNode[] = []
  let lastIndex = 0

  matches.forEach((matchIndex, i) => {
    // Add text before the match
    if (matchIndex > lastIndex) {
      parts.push(text.slice(lastIndex, matchIndex))
    }

    // Add the highlighted character
    parts.push(
      <Box as="span" key={i} bg="yellow.200" color="gray.800">
        {text[matchIndex]}
      </Box>
    )

    lastIndex = matchIndex + 1
  })

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return <>{parts}</>
}

export default function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const { folderTree, selectFolder } = useNavigation()
  const { t } = useAppTranslation('common')

  // Color mode values
  const overlayBg = useColorModeValue('blackAlpha.600', 'blackAlpha.800')
  const contentBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const itemHoverBg = useColorModeValue('gray.50', 'gray.700')
  const selectedItemBg = useColorModeValue('brand.50', 'brand.900')
  const mutedTextColor = useColorModeValue('gray.600', 'gray.400')

  // Build flat list of all folders with their full paths
  const allFolders = useMemo(() => {
    return buildFolderPaths(folderTree)
  }, [folderTree])

  // Filter and sort folders based on query
  const filteredFolders = useMemo((): FolderSearchResult[] => {
    if (!query.trim()) {
      return allFolders.slice(0, 10).map((folder) => ({
        folder,
        score: 1,
        matches: [],
      }))
    }

    const results: FolderSearchResult[] = allFolders
      .map((folder) => {
        const nameMatch = fuzzyMatch(query, folder.name)
        const pathMatch = fuzzyMatch(query, folder.fullPath)
        const bestMatch = nameMatch.score > pathMatch.score ? nameMatch : pathMatch

        return {
          folder,
          score: bestMatch.score,
          matches: bestMatch.matches,
        }
      })
      .filter((result) => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10) // Limit to 10 results

    return results
  }, [query, allFolders])

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [filteredFolders])

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev + 1) % filteredFolders.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev - 1 + filteredFolders.length) % filteredFolders.length)
          break
        case 'Enter':
          e.preventDefault()
          if (filteredFolders[selectedIndex]) {
            handleSelectFolder(filteredFolders[selectedIndex].folder)
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, filteredFolders, selectedIndex, onClose])

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const handleSelectFolder = useCallback(
    (folder: Folder) => {
      selectFolder(folder)
      onClose()
      setQuery('')
    },
    [selectFolder, onClose]
  )

  const handleClose = useCallback(() => {
    onClose()
    setQuery('')
    setSelectedIndex(0)
  }, [onClose])

  return (
    <Portal>
      <Modal isOpen={isOpen} onClose={handleClose} size="2xl" closeOnOverlayClick>
        <ModalOverlay bg={overlayBg} backdropFilter="blur(8px)" />
        <ModalContent
          bg={contentBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
          boxShadow="2xl"
          mt="10vh"
          mx={4}
        >
          <Box p={4}>
            {/* Search Input */}
            <HStack spacing={3} mb={4}>
              <Icon as={FiSearch} color={mutedTextColor} />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('commandPalette.searchPlaceholder')}
                border="none"
                fontSize="lg"
                _focus={{ boxShadow: 'none' }}
                _placeholder={{ color: mutedTextColor }}
              />
              <HStack spacing={1}>
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                <Text fontSize="xs" color={mutedTextColor}>
                  {t('commandPalette.navigateInstructions')}
                </Text>
              </HStack>
            </HStack>

            {/* Results */}
            {filteredFolders.length > 0 ? (
              <VStack spacing={0} align="stretch" maxH="400px" overflowY="auto">
                {filteredFolders.map((result, index) => (
                  <Box
                    key={result.folder.id}
                    p={3}
                    borderRadius="md"
                    cursor="pointer"
                    bg={index === selectedIndex ? selectedItemBg : 'transparent'}
                    _hover={{ bg: itemHoverBg }}
                    onClick={() => handleSelectFolder(result.folder)}
                    transition="background-color 0.1s"
                  >
                    <HStack spacing={3}>
                      <Icon as={FiFolder} color="brand.500" flexShrink={0} />
                      <VStack align="start" spacing={0} flex={1}>
                        <Text fontWeight="medium" fontSize="sm">
                          <HighlightedText text={result.folder.name} matches={result.matches} />
                        </Text>
                        {result.folder.fullPath !== result.folder.name && (
                          <Text fontSize="xs" color={mutedTextColor}>
                            {result.folder.fullPath}
                          </Text>
                        )}
                      </VStack>
                      <Icon as={FiArrowRight} color={mutedTextColor} opacity={0.5} />
                    </HStack>
                  </Box>
                ))}
              </VStack>
            ) : query.trim() ? (
              <Box p={8} textAlign="center">
                <Text color={mutedTextColor}>{t('commandPalette.noFoldersFound', { query })}</Text>
              </Box>
            ) : (
              <Box p={8} textAlign="center">
                <Text color={mutedTextColor}>{t('commandPalette.startTyping')}</Text>
              </Box>
            )}

            {/* Footer */}
            <HStack
              justify="space-between"
              mt={4}
              pt={3}
              borderTop="1px solid"
              borderColor={borderColor}
            >
              <HStack spacing={4} fontSize="xs" color={mutedTextColor}>
                <HStack spacing={1}>
                  <Kbd>Enter</Kbd>
                  <Text>{t('commandPalette.selectInstructions')}</Text>
                </HStack>
                <HStack spacing={1}>
                  <Kbd>Esc</Kbd>
                  <Text>{t('commandPalette.closeInstructions')}</Text>
                </HStack>
              </HStack>
              <Text fontSize="xs" color={mutedTextColor}>
                {t('commandPalette.folderCount', { count: filteredFolders.length })}
              </Text>
            </HStack>
          </Box>
        </ModalContent>
      </Modal>
    </Portal>
  )
}
